import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md font-medium transition-all duration-fast ease-out-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 ring-offset-background disabled:pointer-events-none disabled:opacity-50 active:scale-[0.97] gap-2",
  {
    variants: {
      variant: {
        default: "bg-primary !text-white hover:bg-primary/90 shadow-sm",
        destructive: "bg-danger !text-white hover:bg-danger/90 shadow-sm",
        outline:
          "border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-50 hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:border-neutral-400 dark:hover:border-neutral-600",
        secondary: "bg-neutral-100 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-50 hover:bg-neutral-200 dark:hover:bg-neutral-700",
        ghost: "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-50",
        link: "text-primary dark:text-primary-300 underline-offset-4 hover:underline",
        accent: "bg-accent !text-[#16150F] hover:bg-accent/90 shadow-sm",
        success: "bg-success !text-white hover:bg-success/90 shadow-sm",
        warning: "bg-warning !text-white hover:bg-warning/90 shadow-sm",
      },
      size: {
        xs: "h-9 px-3 text-body-sm rounded-md",
        sm: "h-10 px-3 text-body-sm rounded-md",
        default: "h-11 px-4 text-body rounded-md",
        lg: "h-12 px-6 text-body rounded-md",
        cta: "h-14 px-6 text-body-lg font-semibold rounded-md w-full",
        icon: "h-11 w-11 rounded-md",
        "icon-sm": "h-9 w-9 rounded-md",
        "icon-lg": "h-14 w-14 rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
